import React from 'react';
import { Routes, Route } from 'react-router-dom';

import { PageErrorBoundary } from './ErrorBoundaries';
import {
  Home,
  Auth,
  Subprocessors,
  ClinicDetail,
  Treatments,
  Equipments,
  Blog,
  TreatmentDetail,
  AdminClinics,
  AdminDashboard,
  BlogPost,
  UserDashboard,
  PartnerDashboard,
  Providers,
  ProviderDetail,
  ProvidersLocation,
  PrivacyPolicy,
  TermsConditions,
  CookiePolicy,
  MedicalDisclaimer,
  AntiDiscrimination,
  ComplaintResolution,
  Doctors,
  DoctorDetail,
  MedicalProcedureDetail,
  EquipmentDetail,
  Favorites,
  NotFound,
  ThankYou,
  AboutUs,
  HowItWorks,
  MedipathsLab,
  Glossary,
  AIChatbotPage,
  Changelog,
} from './routeConfig';

const withPageBoundary = (Component: React.ComponentType, source: string) => (
  <PageErrorBoundary source={source}>
    <Component />
  </PageErrorBoundary>
);

export const AppRoutes = () => (
  <Routes>
    <Route path="/" element={withPageBoundary(Home, 'Home')} />
    <Route path="/auth" element={withPageBoundary(Auth, 'Auth')} />
    <Route path="/clinic-detail" element={withPageBoundary(ClinicDetail, 'ClinicDetail')} />
    <Route path="/treatments" element={withPageBoundary(Treatments, 'Treatments')} />
    <Route path="/equipments" element={withPageBoundary(Equipments, 'Equipments')} />
    <Route path="/blog" element={withPageBoundary(Blog, 'Blog')} />
    <Route path="/blog/:slug" element={withPageBoundary(BlogPost, 'BlogPost')} />
    <Route path="/treatment/:slug" element={withPageBoundary(TreatmentDetail, 'TreatmentDetail')} />
    <Route path="/treatment/:slug/thank-you" element={withPageBoundary(ThankYou, 'ThankYou')} />
    <Route path="/admin-clinics" element={withPageBoundary(AdminClinics, 'AdminClinics')} />
    {/* Admin Dashboard - single route with internal hash routing */}
    <Route path="/admin-dashboard" element={withPageBoundary(AdminDashboard, 'AdminDashboard')} />
    <Route path="/admin" element={withPageBoundary(AdminDashboard, 'AdminDashboard')} />
    <Route path="/blog-post" element={withPageBoundary(BlogPost, 'BlogPost')} />
    <Route path="/user-dashboard" element={withPageBoundary(UserDashboard, 'UserDashboard')} />
    <Route path="/dashboard" element={withPageBoundary(UserDashboard, 'UserDashboard')} />
    {/* Partner Dashboard with nested routes for tabs */}
    <Route
      path="/partner-dashboard"
      element={withPageBoundary(PartnerDashboard, 'PartnerDashboard')}
    />
    <Route
      path="/partner-dashboard/providers"
      element={withPageBoundary(PartnerDashboard, 'PartnerDashboardProviders')}
    />
    <Route
      path="/partner-dashboard/treatments"
      element={withPageBoundary(PartnerDashboard, 'PartnerDashboardTreatments')}
    />
    <Route
      path="/partner-dashboard/doctors"
      element={withPageBoundary(PartnerDashboard, 'PartnerDashboardDoctors')}
    />
    <Route
      path="/partner-dashboard/inquiries"
      element={withPageBoundary(PartnerDashboard, 'PartnerDashboardInquiries')}
    />
    <Route
      path="/partner-dashboard/analytics"
      element={withPageBoundary(PartnerDashboard, 'PartnerDashboardAnalytics')}
    />
    <Route path="/providers" element={withPageBoundary(Providers, 'Providers')} />
    <Route path="/provider/:slug" element={withPageBoundary(ProviderDetail, 'ProviderDetail')} />
    <Route path="/provider/:slug/thank-you" element={withPageBoundary(ThankYou, 'ThankYou')} />
    <Route
      path="/providers-location"
      element={withPageBoundary(ProvidersLocation, 'ProvidersLocation')}
    />
    <Route path="/privacy-policy" element={withPageBoundary(PrivacyPolicy, 'PrivacyPolicy')} />
    <Route
      path="/terms-conditions"
      element={withPageBoundary(TermsConditions, 'TermsConditions')}
    />
    <Route path="/cookie-policy" element={withPageBoundary(CookiePolicy, 'CookiePolicy')} />
    <Route
      path="/medical-disclaimer"
      element={withPageBoundary(MedicalDisclaimer, 'MedicalDisclaimer')}
    />
    <Route
      path="/anti-discrimination"
      element={withPageBoundary(AntiDiscrimination, 'AntiDiscrimination')}
    />
    <Route
      path="/complaint-resolution"
      element={withPageBoundary(ComplaintResolution, 'ComplaintResolution')}
    />
    <Route path="/medipaths-lab" element={withPageBoundary(MedipathsLab, 'MedipathsLab')} />
    <Route path="/lab" element={withPageBoundary(MedipathsLab, 'MedipathsLab')} />
    <Route path="/doctors" element={withPageBoundary(Doctors, 'Doctors')} />
    <Route path="/doctor/:slug" element={withPageBoundary(DoctorDetail, 'DoctorDetail')} />
    <Route
      path="/medical-procedure/:slug"
      element={withPageBoundary(MedicalProcedureDetail, 'MedicalProcedureDetail')}
    />
    <Route path="/equipment/:slug" element={withPageBoundary(EquipmentDetail, 'EquipmentDetail')} />
    <Route path="/favorites" element={withPageBoundary(Favorites, 'Favorites')} />
    <Route path="/thank-you" element={withPageBoundary(ThankYou, 'ThankYou')} />
    <Route path="/about-us" element={withPageBoundary(AboutUs, 'AboutUs')} />
    <Route path="/how-it-works" element={withPageBoundary(HowItWorks, 'HowItWorks')} />
    <Route path="/glossary" element={withPageBoundary(Glossary, 'Glossary')} />
    <Route path="/subprocessors" element={withPageBoundary(Subprocessors, 'Subprocessors')} />
    <Route path="/changelog" element={withPageBoundary(Changelog, 'Changelog')} />
    <Route path="/chat/:providerId" element={withPageBoundary(AIChatbotPage, 'AIChatbotPage')} />
    <Route path="/chat" element={withPageBoundary(AIChatbotPage, 'AIChatbotPage')} />
    <Route path="*" element={withPageBoundary(NotFound, 'NotFound')} />
  </Routes>
);
